import { useState, useRef, useEffect } from "react";
import Button from "@mui/material/Button";
import { Box, Stack, Divider, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { utils, writeFile } from "xlsx";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import { useReactToPrint } from "react-to-print";

import dayjs from "dayjs";
import "dayjs/locale/pt-br";

import IconPep from "../../images/pepElaContained.png";
import ButtonGoBack from "../../components/Buttons/outlinedGoback";
import LineChartCustom from "../../components/LineChartCustom";
import { FieldDatePicker } from "../../components/Fields";
import { formatDateISO8601 } from "../../utils/formatarData";
import api from "../../services/api";
import { getItemDecrypt } from "../../utils/cryptoData";

const styles = {
  container: {
    boxShadow: "0px 1px 8px 0px #00000026",
    padding: "32px 32px",
    mt: "32px",
    mb: 5,
    borderRadius: "5px",
    boxSizing: "border-box !important",
  },
  content: {
    alignItems: "center",
    gap: "16px 40px ",
    marginBottom: "24px",
    width: "100%",
    boxSizing: "border-box !important",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  title: {
    fontWeight: "600",
    fontSize: "26px",
    color: "#063F5E",
    lineHeight: "35px",
  },
  subtitle: {
    fontWeight: "700",
    fontSize: "20px",
    lineHeight: "28px",
    color: "#1E729F",
  },
  containerFields: {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    flexWrap: "wrap",
    minWidth: "250px",
  },
  labelGrupoButtons: {
    fontWeight: "500",
    fontSize: "16px",
    color: "#063F5E",
    lineHeight: "19px",
    marginBottom: "7px",
  },
  groupButtons: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    flexWrap: "wrap",
  },
  button: {
    fontWeight: "600",
    fontSize: "20px",
    lineHeight: "28px",
    color: "#1E729F",
    width: "71px !important",
    minWidth: "71px !important",
    height: "50px !important",
    boxSizing: "border-box !important",
    borderRadius: "5px",
    padding: "8px 16px",
  },
  footerText: {
    textAlign: "center",
    fontFamily: "Nunito",
    fontSize: "14px",
    marginTop: "24px",
    color: "#606060",
  },
};

function hasNumber(string) {
  return /\d/.test(string);
}

const HeaderPdf = ({ data }) => {
  const Item = ({ label, text }) => {
    return (
      <>
        <Typography sx={{ fontWeight: 700 }}>{label}:</Typography>
        <Typography sx={{ fontWeight: 500 }} label={label}>
          {text}
        </Typography>
      </>
    );
  };

  return (
    <Box id="HeaderPdf" style={{ display: "none" }}>
      <img src={IconPep} alt="Icon-Pep" width="340px" />
      <Typography
        component="h2"
        sx={{ ...styles.title, textAlign: "center", margin: "30px 0px" }}
      >
        Prontuário Eletrônico para Pacientes com ELA
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr",
          gap: "16px 0px",
          marginBottom: "40px",
        }}
      >
        <Item label="Gerado por" text={data.nome ?? ""} />
        <Item label="Perfil" text={data.perfil ?? ""} />
        <Item label="Município" text={data.municipio ?? ""} />
        <Item
          label="Estabelecimento de Saúde"
          text={data.estabelecimento ?? ""}
        />
        <Item label="Nome do paciente" text={data.nomePaciente ?? ""} />
      </Box>
      <Typography component="h2" sx={{ ...styles.title, marginBottom: "50px" }}>
        Escalas respondidas durante evolução do paciente
      </Typography>
    </Box>
  );
};
const DateNow = new Date();
const DatePast = new Date(DateNow.setMonth(DateNow.getMonth() - 6));

export default function EvolucaoEscala() {
  const [dataInicial, setDataInicial] = useState(formatDateISO8601(DatePast));
  const [dataFinal, setDataFinal] = useState(formatDateISO8601(new Date()));
  const [evolucao, setEvolucao] = useState([]);
  const [difMeses, setDifMeses] = useState("");
  const [headerPdfData, setHeaderPdfData] = useState({});
  const printRef = useRef();
  const { id } = useParams();

  useEffect(() => {
    async function fetchDataPatient() {
      try {
        const storage = getItemDecrypt("vinculo_ativo");
        const { usuario, perfil, uf, municipio, estabelecimento } = storage;
        const { data } = await api.get(`/dadospaciente/${id}`);
        setHeaderPdfData({
          nome: usuario.nome,
          perfil: perfil.nome,
          municipio: `${municipio.nome}/${uf.sigla}`,
          estabelecimento: estabelecimento.nome,
          nomePaciente: data.nome,
        });
      } catch (error) {}
    }
    fetchDataPatient();
  }, [id]);

  useEffect(() => {
    if (
      (dataInicial === null || hasNumber(dataInicial)) &&
      (dataFinal === null || hasNumber(dataFinal))
    )
      fetchData();
  }, [dataInicial, dataFinal]);

  async function fetchData() {
    try {
      const { data } = await api.post("/historico-escalas", {
        id_paciente: id,
        id_tipo_escala: 1,
        dt_inicial: dataInicial,
        dt_final: dataFinal,
      });

      if (data.length > 0) {
        const dtInitial = dayjs(data[0].date, "DD/MM/YYYY");
        const dtFinal = dayjs(data[data.length - 1].date, "DD/MM/YYYY");
        const difMonth = dtFinal.diff(dtInitial, "month");
        setDifMeses(
          difMonth > 1 ? `Últimos ${difMonth} meses` : `Último ${difMonth} mês`
        );
      }
      const response = handleGroupedData(data);
      setEvolucao(response);
    } catch (error) {
      console.error("Ocorreu um erro:", error.message);
    }
  }

  function handleGroupedData(data) {
    const groupedData = data.reduce((acc, current) => {
      let existingEntry = acc.find((entry) => entry.date === current.date);

      if (existingEntry) {
        if (current.createdByPatient) {
          existingEntry.value2 = current.value;
        } else {
          existingEntry.value1 = current.value;
        }
      } else {
        acc.push({
          id: current.id,
          date: current.date,
          value: current.createdByPatient ? null : current.value,
          value2: current.createdByPatient ? current.value : null,
          vinculo_ps_respondente: current.vinculo_ps_respondente
            ? current.vinculo_ps_respondente
            : null,
        });
      }

      return acc;
    }, []);

    return groupedData;
  }

  function ExportDataCsvExcel(filename = "escala.csv") {
    function labelRespondente(item) {
      let data = "";
      if (item.value) {
        data = `${item.vinculo_ps_respondente.perfil} - ${item.vinculo_ps_respondente.name}`;
      }
      return data;
    }
    const dados = evolucao.map((item) => ({
      date: item.date,
      value: item.value,
      paciente: item.value2,
      respondente: labelRespondente(item),
    }));

    // Adicionando informações adicionais
    const now = new Date();
    const info = [
      {
        date: "Título",
        value: "Escalas respondidas durante evolução do paciente",
      },
      {
        date: "Gerado por",
        value: headerPdfData.nome,
      },
      {
        date: "Perfil",
        value: headerPdfData.perfil,
      },
      {
        date: "Município",
        value: headerPdfData.municipio,
      },
      {
        date: "Estabelecimento de Saúde",
        value: headerPdfData.estabelecimento,
      },
      {
        date: "Nome do paciente",
        value: headerPdfData.nomePaciente,
      },
      {
        date: "Data de Geração:",
        value: now.toLocaleDateString(),
      },
      {
        date: "Hora de Geração:",
        value: now.toLocaleTimeString(),
      },
      {
        date: "",
        value: "",
      },
      {
        date: "",
        value: "ESCALA FUNCIONAL",
        paciente: "",
      },
      {
        date: "Data das coletas da escala",
        value: "Valores registrado por profissional",
        paciente: "Valores registrado pelo paciente",
        respondente: "Informação do profissional",
      },
    ];

    var ws = utils.json_to_sheet([...info, ...dados]); // Cabeçalho
    var wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Dados");
    writeFile(wb, filename);
  }

  const handleDownloadImage = async () => {
    try {
      document.querySelector("#HeaderPdf").style.display = "block";
      document.querySelector("#gerado_em").innerHTML =
        new Date().toLocaleString();
      html2canvas(printRef.current, {
        x: -10,
        y: -10,
        width: printRef.current.offsetWidth + 20,
        height: printRef.current.offsetHeight + 20,
      }).then((canvas) => {
        document.querySelector("#HeaderPdf").style.display = "none";
        canvas.toBlob((blob) => {
          saveAs(blob, "escala.png");
        });
        document.querySelector("#gerado_em").innerHTML = "";
      });
    } catch (error) {
      alert("Error ao tentar gerar o PNG");
    }
  };

  const gerarPDF = async () => {
    try {
      document.querySelector("#HeaderPdf").style.display = "block";
      document.querySelector("#gerado_em").innerHTML =
        new Date().toLocaleString();
      handlePrint();
      document.querySelector("#HeaderPdf").style.display = "none";
      document.querySelector("#gerado_em").innerHTML = "";
    } catch (error) {
      alert("Error ao tentar gerar o PDF");
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current, // Conteúdo principal
    documentTitle: "Escalas_evolução_do_paciente",
    pageStyle: `
      @page {
        size: letter;
        margin: 15mm;
      }
    `,
  });

  return (
    <Box>
      <ButtonGoBack />

      <Typography component="h3" style={styles.title}>
        Evolução ALS Functional Rating Scale
      </Typography>

      <Box sx={styles.container}>
        <Box style={styles.subtitle}>
          Escalas respondidas durante evolução do paciente
        </Box>
        <Divider sx={{ mb: 4, mt: 2 }} />

        <Stack sx={styles.content}>
          <Stack sx={styles.containerFields}>
            <Stack minWidth="230px" flex={1}>
              <FieldDatePicker
                label="Data inicial"
                value={dataInicial}
                setValue={(value) => setDataInicial(formatDateISO8601(value))}
                defaultValue={null}
              />
            </Stack>
            <Stack minWidth="230px" flex={1}>
              <FieldDatePicker
                label="Data final"
                value={dataFinal}
                setValue={(value) => setDataFinal(formatDateISO8601(value))}
                defaultValue={null}
              />
            </Stack>
          </Stack>

          <Stack minWidth="308px">
            <Typography component="label" sx={styles.labelGrupoButtons}>
              Exportar dados
            </Typography>
            <Stack sx={styles.groupButtons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => gerarPDF()}
                sx={styles.button}
              >
                Pdf
              </Button>
              <Button
                variant="outlined"
                color="primary"
                sx={{ ...styles.button, textTransform: "none" }}
                onClick={() => ExportDataCsvExcel("Escala.xlsx")}
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                color="primary"
                sx={styles.button}
                onClick={() => ExportDataCsvExcel("Escala.csv")}
              >
                CSV
              </Button>
              <Button
                variant="outlined"
                color="primary"
                sx={styles.button}
                onClick={() => handleDownloadImage()}
              >
                Png
              </Button>
            </Stack>
          </Stack>
        </Stack>
        <Stack ref={printRef}>
          <HeaderPdf data={headerPdfData} />
          <LineChartCustom
            data={evolucao}
            KeyXAxis="date"
            KeyYAxis="value"
            name="Profissional"
            lineSecundary={true}
            KeyYAxislLineSecundary="value2"
            namelineSecundary="Paciente"
          />
          <Typography sx={styles.footerText}>
            {evolucao.length > 0
              ? difMeses
              : "Nenhuma informação de escala encontrada para este paciente."}
          </Typography>
          <Typography
            sx={{ fontWeight: 500, textAlign: "center", margin: "20px 0px" }}
            id="gerado_em"
          ></Typography>
        </Stack>
      </Box>
    </Box>
  );
}
